import { uniqueId } from 'lodash';
import {
  ASPECT_RATIO,
  DEFAULT_HEIGHT,
  DEFAULT_WIDTH,
  INSTAGRAM_CONTENT_TYPES,
  SOCIAL_PLATFORMS,
} from '~/constants';
import {
  ASSET_TYPE,
  IApiSearchAsset,
  ISearchContentResult,
  SearchHighlightedContentResult,
  YOUTUBE_ASSET_SIZE,
} from '~/searchApi/content';

export interface IAssetDimentionsProps {
  assets: IApiSearchAsset[];
  channel?: INSTAGRAM_CONTENT_TYPES;
  platform: SOCIAL_PLATFORMS;
}

export interface ISearchContentMockParams {
  name: string;
  url: string;
  platform: SOCIAL_PLATFORMS;
}

export const getAssetDimensions = ({
  assets,
  channel,
  platform,
}: IAssetDimentionsProps): {
  width: number;
  height: number;
  ratio: string;
} => {
  if (!assets || !assets[0])
    return {
      width: DEFAULT_WIDTH,
      height: DEFAULT_HEIGHT,
      ratio: `${DEFAULT_WIDTH} / ${DEFAULT_HEIGHT}`,
    };

  const asset = assets[0];

  if (!!asset.width && !!asset.height) {
    const { width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT } = asset;

    return { width, height, ratio: `${width} / ${height}` };
  }

  const {
    width = DEFAULT_WIDTH,
    height = DEFAULT_HEIGHT,
    ratio,
  } = ASPECT_RATIO[
    channel ?? (platform as SOCIAL_PLATFORMS.tiktok | SOCIAL_PLATFORMS.youtube)
  ] || {};

  return { width, height, ratio };
};

export const getAsset = ({
  assets,
  platform,
}: {
  assets: IApiSearchAsset[];
  platform: SOCIAL_PLATFORMS;
}) => {
  if (!assets || !Array.isArray(assets) || assets.length === 0) {
    return {};
  }

  if (platform === SOCIAL_PLATFORMS.youtube) {
    const asset = assets.find(({ name }) => name === YOUTUBE_ASSET_SIZE.high);
    if (asset) return asset;
  }

  return assets[0] ?? {};
};

export const generateSearchContentMock = (
  content: ISearchContentMockParams[],
): ISearchContentResult[] => {
  // @ts-ignore - I give up - too many type variations to update
  return content.map(({ name, url, platform }) => {
    const base = {
      assets: [
        {
          url: url,
          type:
            platform === SOCIAL_PLATFORMS.instagram
              ? ASSET_TYPE.image
              : ASSET_TYPE.video,
        },
      ],
      caption: '',
      comments: 0,
      contentId: uniqueId(),
      createdTime: '2023-10-27T06:01:05.000+00:00',
      creator: {
        id: uniqueId(),
        platforms: {
          instagram: {},
        },
        fullName: name,
        status: 'connected',
      },
      engagements: 100000,
      likes: 2000000,
      updatedAt: '2023-10-27T06:01:05.000+00:00',
      views: 123456,
      shares: 123456,
      publishedAt: '2023-10-27T06:01:05.000+00:00',
    };

    if (platform === SOCIAL_PLATFORMS.instagram) {
      return {
        ...base,
        platform: SOCIAL_PLATFORMS.instagram,
        type: ASSET_TYPE.image,
        channel: INSTAGRAM_CONTENT_TYPES.story,
        interactions: 1213333,
        plays: 123456,
        postFollowersEngRate: 123456,
        postReachEngRate: 123456,
        reach: 123456,
        saved: 123456,
        postedAt: '2023-10-27T06:01:05.000+00:00',
      };
    }
    if (platform === SOCIAL_PLATFORMS.tiktok) {
      return {
        ...base,
        embedUrl: url,
        engagementRate: 123456,
        followerEngagementRate: 123456,
        platform: SOCIAL_PLATFORMS.tiktok,
        type: ASSET_TYPE.video,
        createTime: 'string',
        engagementCount: 123456,
        videoViews: 123456,
      };
    }
    return {
      ...base,
      dislikes: 123456,
      favourites: 123456,
      platform: SOCIAL_PLATFORMS.youtube,
      type: ASSET_TYPE.video,
      viewCount: 123456,
      likeCount: 123456,
      dislikeCount: 123456,
      viewRate: 0.125,
      thumbnails: {
        default: {
          url: url,
          height: 400,
          width: 300,
        },
        high: {
          url: url,
          height: 400,
          width: 300,
        },
        medium: {
          url: url,
          height: 400,
          width: 300,
        },
        maxres: {
          url: url,
          height: 400,
          width: 300,
        },
        standard: {
          url: url,
          height: 400,
          width: 300,
        },
      },
      commentCount: 123456,
    };
  });
};

export const getSearchContentsIds = (
  contents: SearchHighlightedContentResult[] | undefined,
) => {
  if (!contents) return [];
  return contents
    .map((content: SearchHighlightedContentResult) => content.id)
    .filter(Boolean);
};
